import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Container, 
  Typography, 
  Paper,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  ButtonGroup,
  Chip,
  Stack,
  Grid
} from '@mui/material';
import { 
  Add as AddIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../CentralSnackbar';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { apiFetch } from '../../utils/apiConfig';
import { getAuthHeaders  } from '../../utils/authUtils';
import { loadFormatConfig, formatTimeSync } from '../../utils/dateTimeFormat';
import './CalendarStyles.css';

// Event-Typ-Definitionen mit Farben für FullCalendar (nur Backend-kompatible Typen)
const getEventTypes = (t) => ({
  duty: { label: t('admin.calendarSystem.eventTypes.duty'), backgroundColor: 'var(--rd-cal-duty)', borderColor: 'var(--rd-cal-duty)', textColor: 'white' },
  'on-call duty': { label: t('admin.calendarSystem.eventTypes.on-call duty'), backgroundColor: 'var(--rd-cal-oncall)', borderColor: 'var(--rd-cal-oncall)', textColor: 'white' },
  vacation: { label: t('admin.calendarSystem.eventTypes.vacation'), backgroundColor: 'var(--rd-cal-vacation)', borderColor: 'var(--rd-cal-vacation)', textColor: 'white' },
  absence: { label: t('admin.calendarSystem.eventTypes.absence'), backgroundColor: 'var(--rd-cal-absence)', borderColor: 'var(--rd-cal-absence)', textColor: 'white' },
  training: { label: t('admin.calendarSystem.eventTypes.training'), backgroundColor: 'var(--rd-cal-training)', borderColor: 'var(--rd-cal-training)', textColor: 'white' },
  blocked: { label: t('admin.calendarSystem.eventTypes.blocked'), backgroundColor: 'var(--rd-cal-blocked)', borderColor: 'var(--rd-cal-blocked)', textColor: 'white' },
  maintenance: { label: t('admin.calendarSystem.eventTypes.maintenance'), backgroundColor: 'var(--rd-cal-maintenance)', borderColor: 'var(--rd-cal-maintenance)', textColor: 'white' }
});

// Diensttyp-Vorlagen für Drag-and-Drop (gemappt auf Backend EventType Enum)
const getServiceTypes = (t) => [
  {
    id: 'duty',
    title: t('admin.calendarSystem.serviceTypes.duty'),
    duration: 8.5,
    color: 'var(--rd-cal-duty)',
    type: 'duty',
    hasTimeRange: true
  },
  {
    id: 'on-call',
    title: t('admin.calendarSystem.serviceTypes.onCall'),
    duration: 12,
    color: 'var(--rd-cal-oncall)',
    type: 'on-call duty',
    hasTimeRange: true
  },
  {
    id: 'vacation',
    title: t('admin.calendarSystem.serviceTypes.vacation'),
    color: 'var(--rd-cal-vacation)',
    type: 'vacation',
    hasTimeRange: false,
    allDay: true
  },
  {
    id: 'absence',
    title: t('admin.calendarSystem.serviceTypes.absence'),
    color: 'var(--rd-text-secondary)',
    type: 'absence',
    hasTimeRange: false,
    allDay: true
  },
  {
    id: 'training',
    title: t('admin.calendarSystem.serviceTypes.training'),
    color: 'var(--rd-cal-training)',
    type: 'training',
    hasTimeRange: true
  },
  {
    id: 'blocked',
    title: t('admin.calendarSystem.serviceTypes.blocked'),
    color: 'var(--rd-cal-blocked)',
    type: 'blocked',
    hasTimeRange: true
  },
  {
    id: 'maintenance',
    title: t('admin.calendarSystem.serviceTypes.maintenance'),
    color: 'var(--rd-cal-maintenance)',
    type: 'maintenance',
    hasTimeRange: true
  }
];

/**
 * Früher ~0.8 für optische Verkleinerung; CSS `zoom` um FullCalendar herum zerstört das Zeitraster.
 * Wert 1 = keine Skalierung. Konstante bleibt, falls noch Referenzen (z. B. nach HMR) im Bundle sind.
 */
const CALENDAR_PAGE_CONTENT_ZOOM = 1;

// Lokalisierung für FullCalendar basierend auf aktueller Sprache
const getCalendarLocale = (language) => {
  if (language === 'de') {
    return {
      code: 'de',
      week: { dow: 1, doy: 4 },
      buttonText: { prev: 'Zurück', next: 'Vor', today: 'Heute', month: 'Monat', week: 'Woche', day: 'Tag', list: 'Liste' },
      weekText: 'KW',
      allDayText: 'Ganztägig',
      moreLinkText: function(n) { return '+ weitere ' + n; },
      noEventsText: 'Keine Ereignisse anzuzeigen'
    };
  } else if (language === 'fr') {
    return {
      code: 'fr',
      week: { dow: 1, doy: 4 },
      buttonText: { prev: 'Précédent', next: 'Suivant', today: 'Aujourd\'hui', month: 'Mois', week: 'Semaine', day: 'Jour', list: 'Liste' },
      weekText: 'Sem',
      allDayText: 'Toute la journée',
      moreLinkText: function(n) { return '+ ' + n + ' autres'; },
      noEventsText: 'Aucun événement à afficher'
    };
  } else {
    return {
      code: 'en',
      week: { dow: 1, doy: 4 },
      buttonText: { prev: 'Previous', next: 'Next', today: 'Today', month: 'Month', week: 'Week', day: 'Day', list: 'List' },
      weekText: 'W',
      allDayText: 'All day',
      moreLinkText: function(n) { return '+ ' + n + ' more'; },
      noEventsText: 'No events to display'
    };
  }
};

const CalendarComponent = () => {
  const { t, i18n } = useTranslation();
  const { showError, showWarning } = useSnackbar();
  const calendarRef = useRef(null);
  
  // Calendar States
  const [view, setView] = useState("timeGridDay");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calendarLocale, setCalendarLocale] = useState(getCalendarLocale(i18n.language));
  
  // Users and modalities for filtering
  const [users, setUsers] = useState([]);
  const modalities = ['CT', 'MRI', 'X-Ray', 'Ultrasound', 'Nuclear Medicine'];
  
  // Filter states
  const [filters, setFilters] = useState({
    user_id: '',
    modality_id: '',
    event_type: ''
  });
  
  // Event dialog states
  const [eventDialog, setEventDialog] = useState({ open: false, mode: 'create', event: null });
  const [eventForm, setEventForm] = useState({
    user_id: '',
    modality_id: '',
    type: 'duty',
    start_time: '',
    end_time: '',
    notes: '',
    color: 'var(--rd-primary)',
    allDay: false
  });

  // Get event types with translations
  const EVENT_TYPES = getEventTypes(t);
  
  // Get service types with translations
  const SERVICE_TYPES = getServiceTypes(t);

  // Reset event form to default state
  const resetEventForm = () => {
    setEventForm({
      user_id: '',
      modality_id: '',
      type: 'duty',
      start_time: '',
      end_time: '',
      notes: '',
      color: 'var(--rd-primary)',
      allDay: false
    });
  };

  // Close dialog and reset form
  const closeDialog = () => {
    setEventDialog({ open: false, mode: 'create', event: null });
    resetEventForm();
  };

  // Handle drop event from shift templates
  const handleDrop = (dropInfo) => {
    try {
      console.log('Drop event:', dropInfo);
      
      let templateData = null;
      
      // Versuche Template-Daten aus verschiedenen Quellen zu bekommen
      if (dropInfo.draggedEl && dropInfo.draggedEl.dataset.template) {
        templateData = JSON.parse(dropInfo.draggedEl.dataset.template);
      } else if (dropInfo.event && dropInfo.event.extendedProps && dropInfo.event.extendedProps.templateData) {
        templateData = dropInfo.event.extendedProps.templateData;
      } else {
        console.error('No template data found', dropInfo);
        showError(t('admin.calendarSystem.errors.noTemplateData', 'Fehler: Keine Template-Daten gefunden'), 'ERR-CAL-001');
        return;
      }
      
      console.log('Template data:', templateData);
      console.log('Drop date:', dropInfo.date);
      
      let startTimeLocal, endTimeLocal, notes;
      
      if (templateData.allDay) {
        // Ganztägiges Event (Urlaub, Abwesenheit)
        const dropDate = new Date(dropInfo.date);
        const year = dropDate.getFullYear();
        const month = String(dropDate.getMonth() + 1).padStart(2, '0');
        const day = String(dropDate.getDate()).padStart(2, '0');
        
        // Für ganztägige Events Zeit von 00:00 bis 23:59 setzen
        startTimeLocal = `${year}-${month}-${day}T00:00`;
        endTimeLocal = `${year}-${month}-${day}T23:59`;
        notes = `${templateData.title} (Ganztägig)`;
      } else {
        // Zeitbasiertes Event (Dienst, Bereitschaft, etc.)
        const startDateTime = new Date(dropInfo.date);
        
        if (templateData.hasTimeRange && templateData.duration) {
          // Events mit Standard-Dauer (Dienst 8,5h, Bereitschaft 12h)
          const minutes = startDateTime.getMinutes();
          // Runde auf nächste 30-Minuten-Marke (0 oder 30)
          const roundedMinutes = minutes < 15 ? 0 : minutes < 45 ? 30 : 0;
          startDateTime.setMinutes(roundedMinutes, 0, 0);
          if (minutes >= 45) {
            startDateTime.setHours(startDateTime.getHours() + 1);
          }
          
          // Berechne Endzeit basierend auf Dauer
          const endDateTime = new Date(startDateTime);
          const durationHours = Math.floor(templateData.duration);
          const durationMinutes = (templateData.duration - durationHours) * 60;
          
          endDateTime.setHours(endDateTime.getHours() + durationHours);
          endDateTime.setMinutes(endDateTime.getMinutes() + durationMinutes);
          
          // Formatiere Zeit für datetime-local
          const formatDateTimeLocal = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
          };
          
          startTimeLocal = formatDateTimeLocal(startDateTime);
          endTimeLocal = formatDateTimeLocal(endDateTime);
          notes = `${templateData.title} (${templateData.duration}h)`;
        } else {
          // Events ohne Standard-Dauer (Schulung, Gesperrt, Wartung)
          const dropTime = new Date(dropInfo.date);
          
          // Standard 2-Stunden-Fenster
          const endTime = new Date(dropTime);
          endTime.setHours(endTime.getHours() + 2);
          
          const formatDateTimeLocal = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
          };
          
          startTimeLocal = formatDateTimeLocal(dropTime);
          endTimeLocal = formatDateTimeLocal(endTime);
          notes = `${templateData.title}`;
        }
      }
      
      console.log('Calculated times:', startTimeLocal, endTimeLocal);
      
      // Bestimme, ob das Event ganztägig sein soll
      const isAllDayEvent = templateData.allDay || 
                           (templateData.type === 'vacation' || templateData.type === 'absence');
      
      // Öffne Event-Dialog mit vorausgefüllten Daten
      setEventForm({
        user_id: users.length > 0 ? users[0].id : '', // Automatisch ersten Benutzer auswählen
        modality_id: '',
        type: templateData.type,
        start_time: startTimeLocal,
        end_time: endTimeLocal,
        notes: notes,
        color: templateData.color,
        allDay: isAllDayEvent
      });
      
      setEventDialog({ open: true, mode: 'create', event: null });
      
    } catch (error) {
      console.error('Error processing drop:', error);
      showError(t('admin.calendarSystem.errors.dropProcessing', 'Fehler beim Verarbeiten der Schicht-Vorlage'), 'ERR-CAL-002');
    }
  };

  // Load date/time format configuration
  useEffect(() => {
    loadFormatConfig();
  }, []);

  // Update calendar locale when language changes
  useEffect(() => {
    const newLocale = getCalendarLocale(i18n.language);
    setCalendarLocale(newLocale);
    
    // Update FullCalendar if it exists
    const calendar = calendarRef.current?.getApi();
    if (calendar) {
      calendar.setOption('locale', newLocale);
    }
  }, [i18n.language]);

  // Flex-/Zoom-Layout: FullCalendar nach Größenänderung neu messen
  useEffect(() => {
    const run = () => {
      const api = calendarRef.current?.getApi();
      if (!api) return;
      requestAnimationFrame(() => {
        try {
          api.updateSize();
        } catch {
          /* noop */
        }
      });
    };
    run();
    const t = window.setTimeout(run, 100);
    const t2 = window.setTimeout(run, 400);
    window.addEventListener('resize', run);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
      window.removeEventListener('resize', run);
    };
  }, [view, loading, events.length]);

  // Initialize external draggable elements
  useEffect(() => {
    const containerEl = document.querySelector('.shift-templates-container');
    const draggableElements = document.querySelectorAll('.shift-template-draggable');
    
    console.log('Initializing draggable elements:', draggableElements.length);
    
    if (containerEl && draggableElements.length > 0) {
      const draggable = new Draggable(containerEl, {
        itemSelector: '.shift-template-draggable',
        eventData: function(eventEl) {
          try {
            console.log('Creating event data for element:', eventEl);
            const templateData = JSON.parse(eventEl.dataset.template);
            console.log('Template data for drag:', templateData);
            
            return {
              title: templateData.title,
              duration: { hours: templateData.duration },
              backgroundColor: templateData.color,
              borderColor: templateData.color,
              textColor: 'white',
              extendedProps: {
                templateData: templateData
              }
            };
          } catch (error) {
            console.error('Error parsing template data:', error);
            return null;
          }
        }
      });

      console.log('Draggable initialized successfully');

      return () => {
        console.log('Destroying draggable');
        draggable.destroy();
      };
    } else {
      console.log('Container or draggable elements not found');
    }
  }, [SERVICE_TYPES]);

  // Load users
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const authHeaders = getAuthHeaders();
        const response = await apiFetch('users/');
        if (response.ok) {
          const userData = await response.json();
          setUsers(userData);
        } else {
          showError(t('admin.calendarSystem.errors.loadUsers'), 'ERR-CAL-003');
        }
      } catch (error) {
        console.error('Error loading users:', error);
        showError(t('admin.calendarSystem.errors.loadUsers'), 'ERR-CAL-004');
      }
    };
    loadUsers();
  }, [showError, t]);

  // Convert backend events to FullCalendar format
  const convertEventsToFullCalendar = (backendEvents) => {
    return backendEvents.map(event => {
      const eventType = EVENT_TYPES[event.type] || EVENT_TYPES.duty;
      const user = users.find(u => u.id === event.user_id);
      
      // Automatisch ganztägige Events erkennen
      const startDate = new Date(event.start_time);
      const endDate = new Date(event.end_time);
      
      // Berechne die Dauer in Stunden
      const durationHours = (endDate - startDate) / (1000 * 60 * 60);
      
      const isAllDay = event.allDay || 
                      (event.type === 'vacation' || event.type === 'absence') ||
                      // Prüfe auf lokale Mitternachtszeiten (vor UTC-Konvertierung)
                      (startDate.getHours() === 0 && startDate.getMinutes() === 0 && 
                       endDate.getHours() === 23 && endDate.getMinutes() === 59) ||
                      // Prüfe auf UTC-konvertierte Zeiten (ca. 23.98 Stunden = 23:59)
                      (durationHours >= 23.9 && durationHours <= 24.1) ||
                      // Prüfe auf 22:00-21:59 Muster (UTC+2 Zeitzone)
                      (startDate.getHours() === 22 && endDate.getHours() === 21);
      
      // Format time as hh:mm (nur für nicht-ganztägige Events)
      const startTime = isAllDay ? null : formatTimeSync(event.start_time);
      const endTime = isAllDay ? null : formatTimeSync(event.end_time);
      
      // Für ganztägige Events: Enddatum = Startdatum + 1 Tag (FullCalendar Standard)
      let formattedStart, formattedEnd;
      if (isAllDay) {
        const startDateOnly = event.start_time.split('T')[0];
        const endDate = new Date(startDateOnly);
        endDate.setDate(endDate.getDate() + 1);
        formattedStart = startDateOnly;
        formattedEnd = endDate.toISOString().split('T')[0];
      } else {
        formattedStart = event.start_time;
        formattedEnd = event.end_time;
      }

      return {
        id: event.id.toString(),
        title: `${user?.username || 'N/A'} - ${eventType.label}`,
        start: formattedStart,
        end: formattedEnd,
        allDay: isAllDay,
        backgroundColor: event.color || eventType.backgroundColor,
        borderColor: event.color || eventType.borderColor,
        textColor: 'white',
        extendedProps: {
          originalEvent: { ...event, allDay: isAllDay },
          startTime,
          endTime,
          eventType: event.type,
          userId: event.user_id,
          username: user?.username || 'N/A',
          notes: event.notes || ''
        }
      };
    });
  };

  // Load events from backend
  const loadEvents = useCallback(async () => {
    if (users.length === 0) return; // Wait for users to load first
    
    setLoading(true);
    
    try {
      const authHeaders = getAuthHeaders();
      
      // Calculate date range based on current view and date
      let rangeStart, rangeEnd;
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      
      switch (view) {
        case "timeGridDay":
          rangeStart = new Date(currentDate);
          rangeStart.setHours(0, 0, 0, 0);
          rangeEnd = new Date(currentDate);
          rangeEnd.setHours(23, 59, 59, 999);
          break;
        case "timeGridWeek":
          const startOfWeek = new Date(currentDate);
          const day = startOfWeek.getDay();
          const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday as start
          rangeStart = new Date(startOfWeek.setDate(diff));
          rangeStart.setHours(0, 0, 0, 0);
          rangeEnd = new Date(rangeStart);
          rangeEnd.setDate(rangeEnd.getDate() + 6);
          rangeEnd.setHours(23, 59, 59, 999);
          break;
        case "dayGridMonth":
        default:
          rangeStart = new Date(currentYear, currentMonth, 1);
          rangeEnd = new Date(currentYear, currentMonth + 1, 0);
          rangeEnd.setHours(23, 59, 59, 999);
          break;
      }
      
      // Build API parameters
      const params = new URLSearchParams({
        start_date: rangeStart.toISOString(),
        end_date: rangeEnd.toISOString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      });
      
      const response = await apiFetch(`calendar/events/?${params}`);
      
      if (response.ok) {
        const eventsData = await response.json();
        const fullCalendarEvents = convertEventsToFullCalendar(eventsData);
        setEvents(fullCalendarEvents);
      } else {
        showError(t('admin.calendarSystem.errors.loadEvents'), 'ERR-CAL-005');
      }
    } catch (error) {
      console.error('Error loading events:', error);
      showError(t('admin.calendarSystem.errors.loadEvents'), 'ERR-CAL-006');
    } finally {
      setLoading(false);
    }
  }, [currentDate, view, filters, users, t, showError]);

  // Load events when dependencies change
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Reload events when language changes to update event labels
  useEffect(() => {
    if (users.length > 0) {
      loadEvents();
    }
  }, [i18n.language, users.length]);

  // Calendar event handlers
  const handleDateSelect = (selectInfo) => {
    const startTime = new Date(selectInfo.start);
    const endTime = new Date(selectInfo.end || startTime.getTime() + 60*60*1000);
    
    setEventForm({
      user_id: '',
      modality_id: '',
      type: 'duty',
      start_time: startTime.toISOString().slice(0, 16),
      end_time: endTime.toISOString().slice(0, 16),
      notes: '',
      color: 'var(--rd-primary)',
      allDay: false
    });
    setEventDialog({ open: true, mode: 'create', event: null });
  };

  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event.extendedProps.originalEvent;
    
    let startTime, endTime;
    if (event.allDay) {
      // Für ganztägige Events: 00:00 bis 23:59 Zeiten setzen
      const date = new Date(event.start_time).toISOString().slice(0, 10);
      startTime = `${date}T00:00`;
      endTime = `${date}T23:59`;
    } else {
      // Für zeitbasierte Events: normale datetime-local Formatierung
      startTime = new Date(event.start_time).toISOString().slice(0, 16);
      endTime = new Date(event.end_time).toISOString().slice(0, 16);
    }
    
    setEventForm({
      user_id: event.user_id,
      modality_id: event.modality_id || '',
      type: event.type,
      start_time: startTime,
      end_time: endTime,
      notes: event.notes || '',
      color: event.color || 'var(--rd-primary)',
      allDay: event.allDay || false
    });
    setEventDialog({ open: true, mode: 'edit', event: event });
  };

  const handleSaveEvent = async () => {
    try {
      // Validierung der Pflichtfelder
      if (!eventForm.user_id) {
        showWarning(t('admin.calendarSystem.errors.selectUser', 'Bitte wählen Sie einen Benutzer aus'));
        return;
      }
      if (!eventForm.start_time || !eventForm.end_time) {
        showWarning(t('admin.calendarSystem.errors.enterTimes', 'Bitte geben Sie Start- und Endzeit an'));
        return;
      }
      
      const authHeaders = getAuthHeaders();
      const eventData = {
        user_id: parseInt(eventForm.user_id),
        modality_id: eventForm.modality_id || null,
        type: eventForm.type,
        start_time: new Date(eventForm.start_time).toISOString(),
        end_time: new Date(eventForm.end_time).toISOString(),
        notes: eventForm.notes || '',
        allDay: eventForm.allDay || false
      };

      console.log('Sending event data:', eventData);
      console.log('Event form state:', eventForm);

      let response;
      if (eventDialog.mode === 'create') {
        response = await apiFetch('calendar/events/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventData)
        });
      } else {
        response = await apiFetch(`calendar/events/${eventDialog.event.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventData)
        });
      }

      console.log('Response status:', response.status);
      
      if (response.ok) {
        console.log('Event saved successfully');
        closeDialog();
        loadEvents(); // Reload events
      } else {
        const errorData = await response.text();
        console.error('Error response:', errorData);
        showError(t('admin.calendarSystem.errors.saveEvent'), 'ERR-CAL-007');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      showError(t('admin.calendarSystem.errors.saveEvent'), 'ERR-CAL-008');
    }
  };

  const handleDeleteEvent = async () => {
    if (!eventDialog.event) return;
    
    try {
      const authHeaders = getAuthHeaders();
      const response = await apiFetch(`calendar/events/${eventDialog.event.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        closeDialog();
        loadEvents(); // Reload events
      } else {
        showError(t('admin.calendarSystem.errors.deleteEvent'), 'ERR-CAL-009');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      showError(t('admin.calendarSystem.errors.deleteEvent'), 'ERR-CAL-010');
    }
  };

  // Calendar navigation helpers
  const getViewTitle = () => {
    const calendar = calendarRef.current?.getApi();
    if (calendar) {
      return calendar.view.title;
    }
    
    // Fallback with current language locale
    let locale = 'en-US';
    if (i18n.language === 'de') locale = 'de-DE';
    else if (i18n.language === 'fr') locale = 'fr-FR';
    
    return currentDate.toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'long',
      ...(view === 'timeGridDay' && { day: 'numeric' })
    });
  };

  const goToday = () => {
    const calendar = calendarRef.current?.getApi();
    if (calendar) {
      calendar.today();
      setCurrentDate(calendar.getDate());
    }
  };

  const goPrev = () => {
    const calendar = calendarRef.current?.getApi();
    if (calendar) {
      calendar.prev();
      setCurrentDate(calendar.getDate());
    }
  };

  const goNext = () => {
    const calendar = calendarRef.current?.getApi();
    if (calendar) {
      calendar.next();
      setCurrentDate(calendar.getDate());
    }
  };

  const changeView = (newView) => {
    setView(newView);
    const calendar = calendarRef.current?.getApi();
    if (calendar) {
      calendar.changeView(newView);
    }
  };

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
      px: { xs: 2, sm: 3 },
      boxSizing: 'border-box',
      height: `calc(100vh - 150px)`,
      maxHeight: `calc(100vh - 150px)`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Paper sx={{ 
        width: '100%', 
        mt: 0,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          p: 2, 
          flex: 1, 
          minHeight: 0, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          width: '100%'
        }}>

          {/* Controls and Filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
            {/* Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button onClick={goPrev} className="button-cancel" size="small">‹</Button>
              <Button onClick={goToday} className="button-cancel" size="small">
                {t('admin.calendarSystem.today')}
              </Button>
              <Button onClick={goNext} className="button-cancel" size="small">›</Button>
              <Typography variant="h6" sx={{ mx: 2, minWidth: 200 }}>
                {getViewTitle()}
              </Typography>
            </Box>

            {/* View Switcher */}
            <ButtonGroup id="buttongroup" size="small">
              <Button 
                onClick={() => changeView('timeGridDay')} 
                variant={view === 'timeGridDay' ? 'contained' : 'outlined'}
              >
                {t('admin.calendarSystem.views.day')}
              </Button>
              <Button 
                onClick={() => changeView('timeGridWeek')} 
                variant={view === 'timeGridWeek' ? 'contained' : 'outlined'}
              >
                {t('admin.calendarSystem.views.week')}
              </Button>
              <Button 
                onClick={() => changeView('dayGridMonth')} 
                variant={view === 'dayGridMonth' ? 'contained' : 'outlined'}
              >
                {t('admin.calendarSystem.views.month')}
              </Button>
            </ButtonGroup>

            <Button
              className="button"
              startIcon={<AddIcon />}
              onClick={() => {
                // Berechne vernünftige Default-Zeiten basierend auf aktueller Zeit
                const now = new Date();
                const currentHour = now.getHours();
                
                let defaultStart, defaultEnd, defaultType;
                
                // Wähle passende Schicht basierend auf aktueller Zeit
                if (currentHour >= 6 && currentHour < 14) {
                  // Frühschicht (06:00-14:30)
                  defaultStart = new Date(now);
                  defaultStart.setHours(6, 0, 0, 0);
                  defaultEnd = new Date(now);
                  defaultEnd.setHours(14, 30, 0, 0);
                  defaultType = 'duty';
                } else if (currentHour >= 14 && currentHour < 22) {
                  // Spätschicht (14:00-22:30)
                  defaultStart = new Date(now);
                  defaultStart.setHours(14, 0, 0, 0);
                  defaultEnd = new Date(now);
                  defaultEnd.setHours(22, 30, 0, 0);
                  defaultType = 'duty';
                } else if (currentHour >= 22 || currentHour < 6) {
                  // Nachtschicht (22:00-06:30 nächster Tag)
                  defaultStart = new Date(now);
                  defaultEnd = new Date(now);
                  
                  if (currentHour >= 22) {
                    // Heute 22:00, morgen 06:30
                    defaultStart.setHours(22, 0, 0, 0);
                    defaultEnd.setDate(defaultEnd.getDate() + 1);
                    defaultEnd.setHours(6, 30, 0, 0);
                  } else {
                    // Gestern 22:00, heute 06:30
                    defaultStart.setDate(defaultStart.getDate() - 1);
                    defaultStart.setHours(22, 0, 0, 0);
                    defaultEnd.setHours(6, 30, 0, 0);
                  }
                  defaultType = 'duty';
                } else {
                  // Fallback: Frühschicht
                  defaultStart = new Date(now);
                  defaultStart.setHours(6, 0, 0, 0);
                  defaultEnd = new Date(now);
                  defaultEnd.setHours(14, 30, 0, 0);
                  defaultType = 'duty';
                }
                
                // Formatiere für datetime-local
                const formatDateTimeLocal = (date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                };
                
                setEventForm({
                  user_id: users.length > 0 ? users[0].id : '',
                  modality_id: '',
                  type: defaultType,
                  start_time: formatDateTimeLocal(defaultStart),
                  end_time: formatDateTimeLocal(defaultEnd),
                  notes: '',
                  color: 'var(--rd-primary)',
                  allDay: false
                });
                setEventDialog({ open: true, mode: 'create', event: null });
              }}
            >
              {t('admin.calendarSystem.createEvent')}
            </Button>
          </Box>

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap', flexShrink: 0 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t('admin.calendarSystem.filters.user')}</InputLabel>
              <Select
                value={filters.user_id}
                onChange={(e) => setFilters(prev => ({ ...prev, user_id: e.target.value }))}
              >
                <MenuItem value="">{t('admin.calendarSystem.filters.allUsers')}</MenuItem>
                {users.map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.full_name || user.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t('admin.calendarSystem.filters.modality')}</InputLabel>
              <Select
                value={filters.modality_id}
                onChange={(e) => setFilters(prev => ({ ...prev, modality_id: e.target.value }))}
              >
                <MenuItem value="">{t('admin.calendarSystem.filters.allModalities')}</MenuItem>
                {modalities.map(modality => (
                  <MenuItem key={modality} value={modality}>
                    {modality}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t('admin.calendarSystem.filters.eventType')}</InputLabel>
              <Select
                value={filters.event_type}
                onChange={(e) => setFilters(prev => ({ ...prev, event_type: e.target.value }))}
              >
                <MenuItem value="">{t('admin.calendarSystem.filters.allEventTypes')}</MenuItem>
                {Object.entries(EVENT_TYPES).map(([value, { label }]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(filters.user_id || filters.modality_id || filters.event_type) && (
              <Button
                className="button-cancel"
                startIcon={<ClearIcon />}
                onClick={() => setFilters({ user_id: '', modality_id: '', event_type: '' })}
                size="small"
              >
                {t('admin.calendarSystem.clearFilters')}
              </Button>
            )}
          </Box>

          {/* Active Filters Display */}
          {(filters.user_id || filters.modality_id || filters.event_type) && (
            <Box sx={{ mb: 1, flexShrink: 0 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {filters.user_id && (
                  <Chip
                    size="small"
                    label={`${t('admin.calendarSystem.filters.user')}: ${users.find(u => u.id === parseInt(filters.user_id))?.full_name || 'N/A'}`}
                    onDelete={() => setFilters(prev => ({ ...prev, user_id: '' }))}
                  />
                )}
                {filters.modality_id && (
                  <Chip
                    size="small"
                    label={`${t('admin.calendarSystem.filters.modality')}: ${filters.modality_id}`}
                    onDelete={() => setFilters(prev => ({ ...prev, modality_id: '' }))}
                  />
                )}
                {filters.event_type && (
                  <Chip
                    size="small"
                    label={`${t('admin.calendarSystem.filters.eventType')}: ${EVENT_TYPES[filters.event_type]?.label || filters.event_type}`}
                    onDelete={() => setFilters(prev => ({ ...prev, event_type: '' }))}
                  />
                )}
              </Stack>
            </Box>
          )}

          {/* Loading */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexShrink: 0 }}>
              <Typography>{t('admin.calendarSystem.messages.loading')}</Typography>
            </Box>
          )}

          {/* FullCalendar — füllt den restlichen Platz */}
          <Box sx={{ 
            flex: '1 1 0px',
            minHeight: 0,
            width: '100%',
            mb: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
              headerToolbar={false}
              initialView={view}
              locale={calendarLocale}
              events={events}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              select={handleDateSelect}
              eventClick={handleEventClick}
              height="100%"
              nowIndicator={true}
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              expandRows={true}
              stickyHeaderDates={false}
              dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
              eventDisplay="block"
              displayEventTime={true}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              slotLabelFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              // Drag and Drop Configuration
              droppable={true}
              drop={handleDrop}
              eventOverlap={true}
              selectOverlap={true}
              eventAllow={() => true}
            />
          </Box>

          {/* Schicht-Vorlagen horizontal unter dem Kalender */}
          <Box sx={{ mt: 0, flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t('admin.calendarSystem.serviceTypes.title')}
            </Typography>
            
            <Box className="shift-templates-container" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}>
              {SERVICE_TYPES.map((template) => (
                <Box
                  key={template.id}
                  draggable="true"
                  data-template={JSON.stringify(template)}
                  className="shift-template-draggable fc-event button-cancel"
                  title={template.title}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/json', JSON.stringify(template));
                    e.dataTransfer.setData('text/plain', template.id);
                    e.dataTransfer.effectAllowed = 'copy';
                    e.currentTarget.dataset.template = JSON.stringify(template);
                  }}
                  onDragEnd={(e) => {}}
                  sx={{
                    flex: '1 1 120px',
                    maxWidth: 140,
                    minHeight: '32px',
                    p: 1,
                    cursor: 'grab',
                    transition: 'all 0.2s',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'translateY(-1px)'
                    },
                    '&:active': {
                      cursor: 'grabbing',
                      transform: 'scale(0.98)'
                    }
                  }}
                >
                  <Typography variant="body2" fontWeight="500">
                    {template.title}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Event Dialog */}
      <Dialog 
        open={eventDialog.open} 
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {eventDialog.mode === 'create' ? t('admin.calendarSystem.dialog.createTitle') : t('admin.calendarSystem.dialog.editTitle')}
        </DialogTitle>
                  <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small" variant="outlined" sx={{ mt: 1 }}>
              <InputLabel 
                id="user-select-label"
                sx={{
                  color: 'rgba(0, 0, 0, 0.6)',
                  '&.Mui-focused': {
                    color: 'var(--rd-primary)'
                  },
                  '&.MuiInputLabel-shrink': {
                    color: 'rgba(0, 0, 0, 0.6)'
                  },
                  '&.Mui-focused.MuiInputLabel-shrink': {
                    color: 'var(--rd-primary)'
                  }
                }}
              >
                {t('admin.calendarSystem.dialog.user')} *
              </InputLabel>
              <Select
                labelId="user-select-label"
                label={`${t('admin.calendarSystem.dialog.user')} *`}
                value={eventForm.user_id}
                onChange={(e) => setEventForm(prev => ({ ...prev, user_id: e.target.value }))}
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.87)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--rd-primary)'
                  }
                }}
              >
                {users.map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.full_name || user.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel 
                  id="type-select-label"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.6)',
                    '&.Mui-focused': {
                      color: 'var(--rd-primary)'
                    },
                    '&.MuiInputLabel-shrink': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    },
                    '&.Mui-focused.MuiInputLabel-shrink': {
                      color: 'var(--rd-primary)'
                    }
                  }}
                >
                  {t('admin.calendarSystem.dialog.type')} *
                </InputLabel>
                <Select
                  labelId="type-select-label"
                  label={`${t('admin.calendarSystem.dialog.type')} *`}
                  value={eventForm.type}
                  onChange={(e) => setEventForm(prev => ({ ...prev, type: e.target.value }))}
                  required
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.87)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--rd-primary)'
                    }
                  }}
                >
                  {Object.entries(EVENT_TYPES).map(([value, { label }]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel 
                  id="modality-select-label"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.6)',
                    '&.Mui-focused': {
                      color: 'var(--rd-primary)'
                    },
                    '&.MuiInputLabel-shrink': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    },
                    '&.Mui-focused.MuiInputLabel-shrink': {
                      color: 'var(--rd-primary)'
                    }
                  }}
                >
                  {t('admin.calendarSystem.dialog.modality')}
                </InputLabel>
                <Select
                  labelId="modality-select-label"
                  label={t('admin.calendarSystem.dialog.modality')}
                  value={eventForm.modality_id}
                  onChange={(e) => setEventForm(prev => ({ ...prev, modality_id: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.87)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--rd-primary)'
                    }
                  }}
                >
                  <MenuItem value="">{t('admin.calendarSystem.dialog.noModality')}</MenuItem>
                  {modalities.map(modality => (
                    <MenuItem key={modality} value={modality}>
                      {modality}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={`${t('admin.calendarSystem.dialog.startTime')} *`}
                type="datetime-local"
                value={eventForm.start_time}
                onChange={(e) => setEventForm(prev => ({ ...prev, start_time: e.target.value }))}
                InputLabelProps={{ 
                  shrink: true,
                  sx: {
                    '&.Mui-focused': {
                      color: 'var(--rd-primary)'
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.87)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--rd-primary)'
                    }
                  }
                }}
                required
              />
              
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={`${t('admin.calendarSystem.dialog.endTime')} *`}
                type="datetime-local"
                value={eventForm.end_time}
                onChange={(e) => setEventForm(prev => ({ ...prev, end_time: e.target.value }))}
                InputLabelProps={{ 
                  shrink: true,
                  sx: {
                    '&.Mui-focused': {
                      color: 'var(--rd-primary)'
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.87)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--rd-primary)'
                    }
                  }
                }}
                required
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                variant="outlined"
                label={t('admin.calendarSystem.dialog.color')}
                type="color"
                value={eventForm.color}
                onChange={(e) => setEventForm(prev => ({ ...prev, color: e.target.value }))}
                InputLabelProps={{
                  sx: {
                    '&.Mui-focused': {
                      color: 'var(--rd-primary)'
                    }
                  }
                }}
                sx={{
                  width: '120px',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.87)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--rd-primary)'
                    }
                  },
                  '& input[type="color"]': {
                    height: '40px',
                    border: 'none',
                    borderRadius: '4px'
                  }
                }}
              />
              
              <Box 
                sx={{ 
                  width: '30px', 
                  height: '30px', 
                  backgroundColor: eventForm.color, 
                  border: '2px solid var(--rd-border)', 
                  borderRadius: '4px',
                  ml: 1
                }} 
              />
            </Box>
            
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={t('admin.calendarSystem.dialog.notes')}
              multiline
              rows={3}
              value={eventForm.notes}
              onChange={(e) => setEventForm(prev => ({ ...prev, notes: e.target.value }))}
              InputLabelProps={{
                sx: {
                  '&.Mui-focused': {
                    color: 'var(--rd-primary)'
                  }
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.87)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--rd-primary)'
                  }
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {eventDialog.mode === 'edit' && (
            <Button onClick={handleDeleteEvent} className="button-delete">
              {t('admin.calendarSystem.dialog.delete')}
            </Button>
          )}
          <Button onClick={closeDialog} className="button-cancel">
            {t('admin.calendarSystem.dialog.cancel')}
          </Button>
          <Button 
            onClick={handleSaveEvent} 
            className="button"
            disabled={!eventForm.user_id || !eventForm.start_time || !eventForm.end_time}
          >
            {eventDialog.mode === 'create' ? t('admin.calendarSystem.dialog.create') : t('admin.calendarSystem.dialog.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarComponent; 